import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './MobileLayout.scss';
import MobileTopNav from '../MobileTopNav';
import MobileBottomNav from '../MobileBottomNav';
import MobileContent from '../MobileContent';
import { QueueContextProvider } from 'presentation/components/Queue/QueueContext';
import { useQueueRequestsQuery } from 'hooks/useQueueRequestsQuery';
import { useAuthContext } from 'context/useAuthContext';
import { useLayoutContext } from 'context/LayoutContext';
import Loading from 'components/Loading';
import MobileRequestDetails from '../MobileContent/MobileRequestDetails/MobileRequestDetails';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import KSLocationModal from 'presentation/components/KSLocationModal';
import classNames from 'classnames';
import { useUserLocationsQuery } from 'hooks/useUserLocationsQuery';
import { useRecentLocations, MAX_RECENT_LOCATIONS } from 'hooks/useRecentLocations';

type Props = {
  children: React.ReactNode;
};

function MobileLayout({ children }: Props): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { locationId: currentRouteLocationId } = useParams();
  const { isFooterVisible } = useLayoutContext();

  // Define all refs first to avoid hooks order issues
  // Use a ref to track the last time we updated the details visibility
  // This prevents rapid toggling of the state
  const lastVisibilityUpdateRef = useRef<number>(Date.now());
  const visibilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Then define state hooks
  const [activeTab, setActiveTab] = useState<'home' | 'requests' | 'kai'>('home');
  // State for controlling rendering and animation
  const [shouldRenderDetails, setShouldRenderDetails] = useState(false);
  const [isDetailsPanelVisible, setIsDetailsPanelVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null); // Track selected request ID
  const trace = true;
  const { user } = useAuthContext();
  const { recentLocations, setRecentLocations } = useRecentLocations();
  const meId = user?.uid;

  // Fetch queue requests data for both tabs to have it ready
  const { data: requests, status: requestsStatus } = useQueueRequestsQuery(undefined, true);

  // Use Queue Context - Can be used to check request existence or pass state down
  const { state: queueState, dispatch: queueDispatch } = useQueueContext();

  // Fetch user locations
  const { data: userLocationsData } = useUserLocationsQuery();
  // Memoize locations, ensure it's HoneLocationUser[]
  const locations = useMemo(() => (userLocationsData || []) as HoneLocationUser[], [userLocationsData]);

  // Find the current location object to get its name
  const currentLocation = useMemo(() => {
    return locations.find(loc => loc.locationId === currentRouteLocationId);
  }, [locations, currentRouteLocationId]);

  // Use actual current location ID for activeLocation prop if needed by modal
  const activeLocation = useMemo(() => currentRouteLocationId, [currentRouteLocationId]);

  // Effect to sync activeTab state with the current URL query parameter
  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (viewParam === 'queue') {
      setActiveTab('requests');
    } else if (viewParam === 'kai') {
      setActiveTab('kai');
    } else {
      // Default to home if 'view' is not 'queue' or 'kai' or not present
      setActiveTab('home');
    }

    // Reset details view when changing main sections via URL (or view param)
    // Keep this part as it's still relevant when the view changes
    setShouldRenderDetails(false);
    setIsDetailsPanelVisible(false);
  }, [searchParams]); // <-- Depend on searchParams now

  // Effect to handle initial details state based *only* on URL params
  useEffect(() => {
    const detailsParam = searchParams.get('details');
    const requestIdParam = searchParams.get('requestId');

    if (detailsParam === 'true' && requestIdParam) {
      // Direct load: Mount and show immediately, no animation
      setSelectedRequestId(requestIdParam);
      setShouldRenderDetails(true);
      setIsDetailsPanelVisible(true);
    } else {
      // Ensure panel is hidden and unmounted if params are not set
      setSelectedRequestId(null);
      setIsDetailsPanelVisible(false);
      setShouldRenderDetails(false);
    }
    // Only depend on searchParams for this effect
  }, [searchParams]);

  // Timeout ref for hiding animation
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Define handlers before useEffects that use them
  const handleHideDetails = useCallback(() => {
    // Start animation
    setIsDetailsPanelVisible(false);
    // Update URL - only remove 'details', keep 'requestId'
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('details');
    setSearchParams(newParams, { replace: true });

    // Clear any existing hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    // Unmount after animation (matching CSS duration)
    const animationDuration = 400;
    hideTimeoutRef.current = setTimeout(() => {
      setShouldRenderDetails(false);
      setSelectedRequestId(null);
    }, animationDuration);
  }, [searchParams, setSearchParams]);

  const handleShowDetails = useCallback(
    (id: string) => {
      // Clear any pending hide animation timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      setSelectedRequestId(id);
      // Mount component first
      setShouldRenderDetails(true);
      // Update URL
      const newParams = new URLSearchParams(searchParams);
      newParams.set('details', 'true');
      newParams.set('requestId', id);
      setSearchParams(newParams, { replace: true });

      // Start animation *after* mounting (defer to next frame)
      requestAnimationFrame(() => {
        setIsDetailsPanelVisible(true);
      });
    },
    [searchParams, setSearchParams] // Ensure setSearchParams is included if used directly
  );

  const handleTabChange = useCallback(
    (tab: 'home' | 'requests' | 'kai') => {
      setActiveTab(tab);
      // Base path is always new-dashboard now for home/requests
      const basePath = `/app/location/${currentRouteLocationId || ''}/new-dashboard`;

      // Navigate to base path first
      navigate(basePath, { replace: true }); // Use replace to avoid history clutter for tab switching

      // Update search params for the view
      const newParams = new URLSearchParams(searchParams);
      if (tab === 'requests') {
        newParams.set('view', 'queue');
      } else if (tab === 'kai') {
        newParams.set('view', 'kai');
      } else {
        newParams.delete('view'); // Remove view param for home
      }
      // Also remove details params when changing tabs
      newParams.delete('details');
      newParams.delete('requestId');
      setSearchParams(newParams, { replace: true });

      // Reset local state related to details
      setShouldRenderDetails(false);
      setIsDetailsPanelVisible(false);
      setSelectedRequestId(null); // Clear selected ID when switching tabs
    },
    [navigate, currentRouteLocationId, searchParams, setSearchParams] // Added searchParams and setSearchParams
  );

  const handleOpenLocationModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseLocationModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleLocationClick = useCallback(
    (selectedLocationId: string) => {
      // Base path is always new-dashboard
      const targetPath = `/app/location/${selectedLocationId}/new-dashboard`;

      // Preserve the 'view' parameter based on the *current* activeTab before navigation
      const currentView = searchParams.get('view');
      const newParams = new URLSearchParams(); // Start fresh for new location
      if (currentView === 'queue') {
        newParams.set('view', 'queue');
      } // Add other params to preserve if needed

      navigate({ pathname: targetPath, search: newParams.toString() });
      setIsModalOpen(false); // Close the modal after navigating

      // Reset details view state
      setShouldRenderDetails(false);
      setIsDetailsPanelVisible(false);
      setSelectedRequestId(null);
    },
    [navigate, searchParams] // Removed activeTab dependency, using searchParams instead
  );

  const handleAddToRecentLocations = (locationId: string) => {
    const selectedLocation = locations.find(loc => loc.locationId === locationId);
    if (!selectedLocation) return;
    setRecentLocations((prevRecentLocations: HoneLocationUser[]) => {
      const updatedLocations = [selectedLocation, ...prevRecentLocations.filter(loc => loc.locationId !== locationId)];
      const newRecentLocations = updatedLocations.slice(0, MAX_RECENT_LOCATIONS);
      localStorage.setItem('recentLocations', JSON.stringify(newRecentLocations));
      return newRecentLocations;
    });
  };

  const handleRemoveFromRecentLocations = (locationId: string) => {
    const selectedLocation = locations.find(loc => loc.locationId === locationId);
    if (!selectedLocation) return;
    setRecentLocations((prevRecentLocations: HoneLocationUser[]) => {
      const updatedLocations = prevRecentLocations.filter(loc => loc.locationId !== locationId);
      const newRecentLocations = updatedLocations.slice(0, MAX_RECENT_LOCATIONS);
      localStorage.setItem('recentLocations', JSON.stringify(newRecentLocations));
      return newRecentLocations;
    });
  };

  if (requestsStatus === 'pending') {
    return <Loading />;
  }

  if (requestsStatus === 'error') {
    return <div>An error has occurred while getting requests</div>;
  }

  return (
    <QueueContextProvider meId={meId} requests={requests}>
      <div className="MobileLayout">
        <div className="MobileLayout-main">
          <div className="MobileLayout-header">
            <MobileTopNav
              activeTab={activeTab}
              locationName={currentLocation?.locationName || 'Select Location'}
              userName={currentLocation?.name || ''}
              onLocationClick={handleOpenLocationModal}
              onTabChange={handleTabChange}
            />
          </div>

          <div className="MobileLayout-content">
            <MobileContent activeTab={activeTab} trace={trace} onShowDetails={handleShowDetails} />
          </div>

          {isFooterVisible && (
            <div className="MobileLayout-footer">
              <MobileBottomNav activeTab={activeTab} onTabChange={handleTabChange} />
            </div>
          )}
        </div>

        {/* Conditionally Render Details Panel */}
        {shouldRenderDetails && selectedRequestId && (
          <MobileRequestDetails
            // Apply animation class based on visibility state
            className={`mobile-request-details ${isDetailsPanelVisible ? 'visible' : ''}`}
            requestId={selectedRequestId}
            onClose={handleHideDetails}
          />
        )}
      </div>
      {/* Add stopPropagation to prevent clicks inside the modal area from bubbling up */}
      <div
        className={classNames('MobileLayout-locationModal', { open: isModalOpen })}
        onClick={e => e.stopPropagation()}
      >
        <KSLocationModal
          handleClose={handleCloseLocationModal}
          recentLocations={recentLocations}
          activeLocation={activeLocation}
          onLocationChange={handleLocationClick}
          onAddToRecentLocations={handleAddToRecentLocations}
          onRemoveFromRecentLocations={handleRemoveFromRecentLocations}
          isOpen={isModalOpen}
          locations={locations}
          mobile
          autoFocus={false}
        />
      </div>
    </QueueContextProvider>
  );
}

export default MobileLayout;
